import * as PIXI from "pixi.js";
import { COLLECT_SYMBOL_ID } from "../../SymbolData";
import { Settings } from "../../settings";
import SymbolStripSpinner from "./SymbolStripSpinner";
import {
  registerSkippableSpin,
  releaseSkippableSpin,
} from "./spinSkipRegistry";
import { MYSTERY_SKIP_GROUP } from "./mysterySkip";
import { mysteryDramaSound } from "./mysteryDrama";
import { cellCenterIn } from "./symbolGrid";
import { baseDiag } from "../../utils/fsDiag";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME, SFX_CUT_FADE_MS } from "../../sounds";
import { acquireLoopSound, releaseLoopSound } from "./loopSoundRefs";

/**
 * The track the win celebration plays on.
 *
 * Never 0 — that one is the cell's own sequence, and after it lands, the light
 * chase that runs for the rest of the feature. See playWin.
 */
const WIN_TRACK = 1;

/** Mix out of the win, in seconds. Long enough not to snap, short enough that
 * a line change reads as immediate. */
const WIN_MIX_S = 0.12;

// ── One landing sound per frame, however many cells land in it ──
//
// The first landing is a wave — the tracks stop 750ms apart — but a REFILL is
// not: every cell a magnet emptied respins together and stops in the same frame,
// so four coins would play `c_in` four times over each other for nothing.
//
// Exactly the guard useLandingAnimations keeps for super turbo, where all five
// reels land in one frame, and written the same way: a Set cleared by a single
// rAF. Module level because it is a property of the FRAME, not of a cell — a
// per-cell copy could never see the other cells.
const _frameSounds = new Set();
let _frameSoundRAF = null;

const playLandingSoundOncePerFrame = (name) => {
  if (!name || _frameSounds.has(name)) return;
  _frameSounds.add(name);
  soundManager.play(name);
  if (_frameSoundRAF) return;
  _frameSoundRAF = requestAnimationFrame(() => {
    _frameSounds.clear();
    _frameSoundRAF = null;
  });
};

// The size every symbol skeleton in this game is authored at. A cell scales by
// cellHeight / this, which is the reels' own rule.
const SYMBOL_REFERENCE_HEIGHT = Settings.SYMBOL_HEIGHT || 194;

/**
 * GoldenMysteryCellController — one cell, three things NESTED in it.
 *
 * The Mystery opens onto B2, and B2 is not a symbol that stands there: it is a
 * respin happening inside the cell. Three layers do it, and the nesting is the
 * whole point — each one lives in the window of the one above:
 *
 *   my  (the frame)                  doors open with `reveal_spin`, the clip
 *    └── attachment_slot_symbol      that parts them and KEEPS the frame, the
 *         └── myg  (the lid)         backing and the twelve lights. Then
 *              └── attachment_slot_  `anticipation_spin` loops the light chase
 *                   symbol           for as long as the track runs.
 *                   └── the TRACK
 *
 * `myg` is INSIDE the mystery's window, not a second spine over the cell. Put
 * it beside the frame and it covers the whole cell — the golden sign floats on
 * top of the doors instead of being the thing behind them. The mystery's window
 * is what the doors open onto, so that is where its contents belong: the same
 * slot, the same holder/fit pair, the same maths that puts a revealed symbol in
 * there for B1.
 *
 * `myg.reveal` then brings the sign to life, bursts, booms, and deletes itself —
 * keying its OWN `attachment_slot_symbol` up to full alpha on its final frame.
 * It is a lid that blows off, and the track is already spinning behind it, so
 * the boom exposes a moving track rather than starting one.
 *
 * ── The one thing that must never happen ──
 *
 * `reveal` is a ONE-SHOT whose last frame has the golden art at alpha 0. Spine
 * keeps applying that frame for as long as the track entry lives, which is what
 * holds the lid open. Clear that track — a sweep, a `setToSetupPose`, anything —
 * and the golden sign snaps back at FULL ALPHA over the spinning track. So the
 * entry is pinned with `trackEnd = Number.MAX_VALUE` and the caller keeps the
 * cell exempt from the orphan sweep for the whole feature, not just for the
 * length of the clip.
 *
 * Everything game-specific — clip names, the strip's contents, the timings —
 * comes from the config, so this class stays the mechanism and nothing else.
 */
class GoldenMysteryCellController {
  /**
   * @param {Object}   p
   * @param {Object}   p.frameSpine The `my` host — the pooled cell spine.
   * @param {Function} p.makeGolden Builds the `myg` spine into a parent, the
   *        same factory shape the B1 reveal uses for its revealed symbol. The
   *        lid is BUILT here rather than borrowed from the pool, because a slot
   *        object lives in the fit's unit space and a pooled spine is positioned
   *        and scaled for the cell.
   * @param {Object}   p.cellContainer This cell's overlay container. ONE thing
   *        comes from it: its position, which is this cell's point on the grid.
   *        See gridCenter.
   * @param {Object}   p.config     See buildGoldenMysteryConfig.
   * @param {number}   p.cellWidth
   * @param {number}   p.cellHeight
   * @param {Function} [p.diag] Which trace this cell logs in. The class is
   *        shared by both games and logged as `[BASE]` from either, which reads
   *        as a base round running inside the bonus room; the free-spin overlay
   *        controller passes `fsDiag` instead. Same flag either way.
   */
  constructor({
    frameSpine,
    lidSpine = null,
    makeGolden,
    makeFeatureSpine,
    cellContainer,
    config,
    cellWidth,
    cellHeight,
    diag = baseDiag,
  }) {
    this.diag = diag;
    this.frameSpine = frameSpine;
    // ── The ordinary mystery, sitting ON TOP of the frame ──
    //
    // The cell's own `my` spine, taken from the same pool. It plays the
    // build-up and the doors and then fades away, uncovering the frame below.
    // Null only when the pool could not supply one — play() then falls back to
    // the frame opening its own doors, which is what it always did.
    this.lidSpine = lidSpine;
    // This cell's own overlay container. Kept for ONE reason: React positions
    // it from the layout and nothing animates it, so its position IS this
    // cell's point on the grid. See gridCenter.
    this.cellContainer = cellContainer || null;
    this.makeGolden = makeGolden;
    // Builds a live `mul`, `cl` or `upgrade` over the cell — see playCellSpine.
    this.makeFeatureSpine = makeFeatureSpine;
    this._collectSpine = null;
    // The total drawn over the live collector — see showCollectedTotal.
    this._collectNumber = null;
    this.goldenSpine = null;
    this.config = config || {};
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;

    this.strip = null;
    // This cell's share of the shared strip loop — see _startSpinSound.
    this._spinSoundOn = false;
    // The frame's window: what holds the lid.
    this.frameHolder = null;
    this.frameFit = null;
    this._frameSlotName = null;
    // The lid's window: what holds the track.
    this.holder = null;
    // Cancels the bone chain so everything under it is in cell pixels — the
    // same two-container pair RevealSymbolController builds, for the same
    // reason. See _applyFit.
    this.fit = null;
    // Unmasked, for anything drawn to spill past the cell — see _injectGolden.
    this.effects = null;
    // The same, but a layer higher — see _injectGolden.
    this.aboveEffects = null;
    this._slotName = null;
    this.maskGraphic = null;
    this.destroyed = false;
    // Set only while this cell's spin clock is running — see _waitForSpin.
    this._skipId = null;
    this._listeners = [];
    // rAF handle for the deferred hide — see _hideStripNextFrame.
    this._pendingHide = null;
    // When the frame was put to sleep under the lid, so the chase can be
    // fast-forwarded to where it would have been when it is uncovered.
    this._frameSleptAt = null;
    // The spine playCellSpine currently has on the cell, with the container it
    // is parented to and whatever it wears. Held so resize() can reach it —
    // declared here rather than sprung into existence, because a field a resize
    // depends on should be visible in one place.
    this._live = null;
    // The collector's live spine and its number, plus the total the number
    // shows — kept so a resize can rebuild it at the new size.
    this._collectSpine = null;
    this._collectNumber = null;
    this._collectTotal = null;
    this._collectSettled = false;
    // ── Every promise this cell is holding open ──
    //
    // A clip's promise is resolved by a Spine listener, and destroy() removes
    // those listeners — so without this a cell destroyed mid-clip left its
    // awaiter waiting forever. That awaiter is `play()`, whose caller holds
    // `criticalAnimationPlaying` and releases it after the await: no spin
    // button, for the rest of the session, with nothing on screen to say why.
    //
    // SymbolStripSpinner already got this right — its destroy() resolves
    // `_stopResolve` — and this is the same discipline applied to the three
    // promises here.
    this._pending = new Set();
  }

  /**
   * Register a promise's resolver so destroy() can release it.
   *
   * @param   {Function} resolve
   * @returns {Function} Call instead of `resolve` — settles once and forgets.
   */
  _settler(resolve) {
    const settle = () => {
      this._pending.delete(settle);
      resolve();
    };
    this._pending.add(settle);
    return settle;
  }

  // ── The lid, inside the frame's window ───────────────────────────────────

  /**
   * Put the `myg` spine into the mystery's `attachment_slot_symbol`.
   *
   * Exactly the injection B1 makes for a revealed symbol: holder for the slot
   * to move, fit to cancel the bone chain, and the spine sized by the reels'
   * own rule so it matches the symbols around it.
   *
   * @returns {boolean} false when the frame has no window to inject into.
   */
  _injectGolden() {
    const slots = this.config.frame?.slotNames;
    this._frameSlotName = this._resolveSlot(this.frameSpine, slots);
    if (!this._frameSlotName || typeof this.makeGolden !== "function") {
      console.warn("[GoldenMystery] No frame slot found:", slots);
      return false;
    }

    this.frameHolder = new PIXI.Container();
    this.frameHolder.name = "golden_mystery_frame_holder";
    this.frameFit = new PIXI.Container();
    this.frameFit.name = "golden_mystery_frame_fit";
    this.frameHolder.addChild(this.frameFit);

    this.frameSpine.addSlotObject(this._frameSlotName, this.frameHolder);
    this._applyFrameFit();

    // ── ONE host, not two ──
    //
    // This used to build a second spine — `myg` — inside the frame's window,
    // because the frame was the old `my` rig and `myg` had no frame of its own.
    // `myg` has one now (`scatter1_frame`, its own bamboo lights, its own mask
    // pair) so it IS the host: the pool hands it over as `frameSpine` and there
    // is nothing left to nest inside it.
    //
    // `goldenSpine` stays as the name every other method reaches for, pointed
    // at the same object. That keeps this change to the two injection sites
    // instead of rewriting 14 call sites for no behavioural gain.
    //
    // `makeGolden` is still honoured when the pooled host is NOT the golden rig
    // — nothing does that today, but it is the difference between degrading and
    // opening onto nothing if a pool ever hands over the wrong spine.
    const hostIsGolden = !!this._resolveSlot(this.frameSpine, [
      "golden_myst",
    ]);
    this._singleHost = hostIsGolden;
    this.goldenSpine = hostIsGolden
      ? this.frameSpine
      : this.makeGolden({
          parent: this.frameFit,
          cellWidth: this.cellWidth,
          cellHeight: this.cellHeight,
        });

    // ── Effects live OUTSIDE the mask, and ON TOP OF THE LID ──
    //
    // The track's own container is masked to exactly one cell, which is right
    // for a strip — a symbol scrolling past must be cut at the window's edge.
    // It is wrong for everything else: a collector's glow, an upgrade burst and
    // a multiplier's flash are all drawn to spill past the symbol, and inside
    // that mask they are sliced off at the cell border.
    //
    // So they get their own container in the frame's window, in the same cell
    // pixels and with no mask at all.
    //
    // ── The order is the point, and it was wrong ──
    //
    // Added AFTER the lid, because the lid holds the track and the track is the
    // symbol. An effect added before it is drawn UNDERNEATH the coin it is
    // supposed to be bursting over — which is exactly what the upgrade burst
    // was doing. It was not visible as a bug for the multiplier or the
    // collector because both of those hide the flat cell while they play; the
    // upgrade burst deliberately does not, since it is an effect ON the coin.
    this.effects = new PIXI.Container();
    this.effects.name = "golden_mystery_effects";
    this.frameFit.addChild(this.effects);

    // ── And the live spines go ABOVE THE FRAME, not inside its window ──
    //
    // `effects` above is inside `attachment_slot_symbol`, and that slot is index
    // 3 of 29 in `my`: twenty-five slots draw on top of it — the frame itself,
    // both doors, all twelve lights, the three bursts. Right for the coin, which
    // belongs INSIDE the window. Wrong for an effect that has to read as
    // happening ON that coin, because the frame is a full square of art and the
    // burst was underneath every pixel of it.
    //
    // The cell's own overlay container is the layer above all of it, and it is
    // the same space: plain cell pixels, origin at the cell centre, positioned by
    // the layout with nothing animating it (which is why gridCenter measures it).
    // So a spine built for the window needs no change at all to be built here.
    //
    // zIndex 20: over the pooled win spines at 10 and the expanding wild at 15,
    // the two other things that draw into this container.
    if (this.cellContainer && !this.cellContainer.destroyed) {
      this.aboveEffects = new PIXI.Container();
      this.aboveEffects.name = "golden_mystery_effects_above";
      this.aboveEffects.zIndex = 20;
      this.cellContainer.sortableChildren = true;
      this.cellContainer.addChild(this.aboveEffects);
    }

    return !!this.goldenSpine;
  }

  /**
   * The container a reveal on this cell must play INSIDE.
   *
   * The frame's window — the same space the golden spine itself lives in, at
   * the same cell size. A spine parented here is drawn by the frame's slot
   * bone, so it sits within the golden box and moves with it.
   *
   * Without this a reveal on a Golden cell fetches its own pooled spine, which
   * is positioned against the whole cell in the overlay container. Being a
   * sibling of the frame rather than a child of its window, it covers the frame
   * edge to edge: the scatter appears to climb out of the golden box and stand
   * on top of it.
   *
   * `frameFit` and not `frameHolder`: the fit cancels the bone chain's scale,
   * so a child of it is measured in the reels' own pixels. That is the whole
   * reason the golden spine is built there too.
   *
   * @returns {PIXI.Container|null} null until the lid has been injected.
   */
  revealHost() {
    return this.frameFit ?? null;
  }

  /** _applyFit, for the frame's own window. See _applyFit. */
  _applyFrameFit() {
    if (!this.frameFit || !this.frameSpine) return;
    const bone = this.frameSpine.skeleton?.findSlot?.(this._frameSlotName)?.bone;
    const boneScale = bone ? Math.hypot(bone.a, bone.c) : 1;
    const spineScale = this.frameSpine.scale?.x || 1;
    const total = boneScale * spineScale;
    this.frameFit.scale.set(total ? 1 / total : 1);
  }

  // ── Spine helpers ────────────────────────────────────────────────────────

  /** First clip in `candidates` that `spine` actually has. */
  _resolve(spine, candidates) {
    const animations = spine?.skeleton?.data?.animations;
    if (!animations) return null;
    for (const name of candidates || []) {
      if (animations.find((a) => a.name === name)) return name;
    }
    return null;
  }

  /**
   * Play a clip once and resolve when it ends.
   *
   * Resolves immediately when the clip does not exist, so a rig missing one
   * phase skips it instead of hanging the sequence — and listens for `end` as
   * well as `complete`, so a track that is replaced or interrupted cannot leave
   * the chain waiting for an event that will never arrive.
   */
  /**
   * @param {Spine} spine
   * @param {Array|string} candidates
   * @param {object} [skip] Makes this clip skippable while it runs — a press
   *   ENDS it on the frame it arrives and this promise resolves. See
   *   RevealSymbolController._playOnce, which does the same for the ordinary
   *   mystery, and mysterySkip for why cutting is safe here.
   */
  _playOnce(spine, candidates, skip = null, onEvent = null) {
    const name = this._resolve(spine, candidates);
    if (!name || !spine?.state) return Promise.resolve();

    return new Promise((resolve) => {
      const settle = this._settler(resolve);
      const entry = spine.state.setAnimation(0, name, false);

      // ── The frame before the first frame ──
      //
      // A track set here does not reach the skeleton until the next tick, so
      // whatever pose it was in paints once first. Between two clips that is a
      // flicker; on a spine that has just been created it is a whole frame of
      // setup art.
      //
      // Forcing the state onto the skeleton now, and only then raising alpha,
      // means the first frame anyone sees is the first frame of the clip. It is
      // what showWinAnimation does, in the same words: make visible AFTER the
      // animation is queued.
      try {
        spine.state.update(0);
        spine.state.apply(spine.skeleton);
      } catch {
        // A skeleton that cannot be posed will simply show its setup frame.
      }
      spine.alpha = 1;

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        // However the clip ended, the entry comes off the list — a dead cancel
        // left there swallows a press. A no-op when the skip already took it.
        releaseSkippableSpin(this._skipId);
        this._skipId = null;
        spine.state.removeListener(listener);
        this._listeners = this._listeners.filter((l) => l.listener !== listener);
        settle();
      };
      const listener = {
        // One named spine event, handed back to the caller. Same hook and
        // same reason as RevealSymbolController's: the ANIMATOR marks the
        // frame, the code obeys it.
        event: (source, spineEvent) => {
          if (!onEvent || source !== entry) return;
          if (spineEvent?.data?.name !== onEvent.name) return;
          onEvent.fire();
        },
        complete: (completed) => {
          if (completed === entry) finish();
        },
        end: (ended) => {
          if (ended === entry) finish();
        },
      };
      this._listeners.push({ spine, listener });
      spine.state.addListener(listener);

      if (skip && entry) {
        // ── On `this`, not a local — see RevealSymbolController._playOnce ──
        //
        // `destroy()` releases this field and nothing else, so an id held in a
        // closure survives a teardown and swallows the next press. Safe to
        // share with `_waitForSpin`'s id: the build-up is over before a strip
        // exists.
        releaseSkippableSpin(this._skipId);
        this._skipId = registerSkippableSpin(
          skip.order ?? 0,
          () => {
            // Already off the list — skipNextSpin deletes before it cancels.
            this._skipId = null;
            // Resolve NOW. `_injectGolden` and the doors' setAnimation both run
            // synchronously after this, so the track is replaced before the
            // renderer sees the pose this interrupted.
            finish();
          },
          { group: skip.group },
        );
      }
    });
  }

  /**
   * Play several clips back to back on one track, as ONE performance.
   *
   * ── Why not just await them one at a time ──
   *
   * `await playOnce("bounce"); await playOnce("activation")` looks equivalent
   * and is not. It waits for `complete`, returns to JavaScript, and only then
   * queues the next clip — so the skeleton sits on the last frame of the first
   * one for however long that round trip takes. At best it is a held frame; with
   * a default mix time it is a crossfade between two poses that were never meant
   * to blend. Either way it reads as a flash.
   *
   * Spine has a queue for this. `addAnimation` hands the track the next clip
   * BEFORE the current one ends, so the engine runs them as a single sequence
   * with no gap and nothing for us to be late for.
   *
   * Mix is forced to zero for the same reason: these clips are cut to meet
   * exactly — bounce's last frame IS activation's first — so blending them
   * invents a pose the animator never drew.
   *
   * @param   {object}   spine
   * @param   {string[]} clips Clip names, in order. Missing ones are skipped.
   * @param   {object}   [p]
   * @param   {string}   [p.onEventName] A Spine event to listen for, on any clip
   *          in the sequence.
   * @param   {Function} [p.onEvent] Called when that event fires — and, as a
   *          backstop, when the sequence ends without it ever firing. A clip
   *          that carries a state change must never swallow it.
   * @param   {Function} [p.onClipStart] Called with a clip's name the frame it
   *          takes the track. The clips are chained with `addAnimation`, so this
   *          is the only way to know when the SECOND one begins — a caller that
   *          waits for the whole sequence has already missed it.
   * @returns {Promise<void>} resolves when the LAST one ends.
   */
  _playSequence(
    spine,
    clips,
    { onEventName = null, onEvent = null, onClipStart = null } = {},
  ) {
    const names = (clips || [])
      .map((c) => this._resolve(spine, [c]))
      .filter(Boolean);
    // Asked vs resolved, because a clip missing from the rig makes this method
    // return instantly and look identical to one that played and was not
    // noticed. That ambiguity cost an afternoon on the collector.
    if (!names.length || !spine?.state) {
      onEvent?.();
      return Promise.resolve();
    }

    // No crossfade between clips that were authored to butt together.
    try {
      if (spine.state.data) spine.state.data.defaultMix = 0;
    } catch {
      // Older runtimes expose it differently; a default mix is a nicety here.
    }

    return new Promise((resolve) => {
      const settle = this._settler(resolve);
      // ── Exactly what showLandingAnimation does, and why ──
      //
      // clearTracks() then addAnimation for EVERY clip, including the first.
      // `setAnimation` starts a mix from whatever the track was playing; from a
      // cleared track there is nothing to mix from, so the first frame is the
      // first frame.
      spine.state.clearTracks();
      let last = null;
      for (const name of names) {
        last = spine.state.addAnimation(0, name, false, 0);
      }

      let done = false;
      let fired = false;
      const finish = () => {
        if (done) return;
        done = true;
        spine.state.removeListener(listener);
        this._listeners = this._listeners.filter((l) => l.listener !== listener);
        if (onEvent && !fired) onEvent();
        settle();
      };
      const listener = {
        start: (entry) => {
          const name = entry?.animation?.name;
          if (name) onClipStart?.(name);
        },
        event: (_entry, event) => {
          if (!onEventName || event?.data?.name !== onEventName) return;
          if (fired) return;
          fired = true;
          onEvent?.();
        },
        complete: (entry) => {
          if (entry === last) finish();
        },
        end: (entry) => {
          if (entry === last) finish();
        },
      };

      // ── LISTENING BEFORE ANYTHING CAN FIRE ──
      //
      // Attached above the update below, and that is not tidiness. The update is
      // what promotes the first queued clip to current, so the first clip's
      // `start` fires INSIDE it. Registered afterwards, that one start is always
      // missed.
      //
      // It went unnoticed for as long as every sequence began with `bounce`: the
      // missed start was the arrival's, which nothing listens for. The collect
      // is `bounce` then `activation` and fires its coins on activation's start,
      // so it worked — until the landing took the arrival over and `activation`
      // became the first clip. Then the one start being dropped was the one
      // carrying the whole phase, and the magnet took the coins with no
      // lightning at all.
      this._listeners.push({ spine, listener });
      spine.state.addListener(listener);

      // ── update(1/60), NOT update(0) ──
      //
      // A queued entry is promoted to current during an update, and a zero
      // update does not advance the queue — so the pose applied is the one
      // BEFORE the clip, and the first painted frame is stale. The landing path
      // uses 1/60 and says so: "force first frame — use the same time step as
      // texture extraction so the overlay's initial pose exactly matches the
      // static texture".
      try {
        spine.state.update(1 / 60);
        spine.state.apply(spine.skeleton);
      } catch {
        // A skeleton that cannot be posed will show its setup frame.
      }
      spine.alpha = 1;

      // ── The FIRST clip's start is announced here, not by the listener ──
      //
      // The update above is what starts it, and whether Spine's own `start`
      // event survives to a listener registered around that call is a detail of
      // the runtime's event queue — it did not, and chasing it is not worth it
      // when the fact is already known here: names[0] just became current.
      //
      // This is the difference between the collect firing its lightning and not.
      // The phase hangs its whole volley off `activation` starting, and for as
      // long as the sequence was `bounce` then `activation` the listener caught
      // it because it was the SECOND clip. The landing took the arrival over,
      // `activation` became first, and the cue vanished.
      //
      // Only the first, because every later clip starts on an ordinary tick with
      // the listener long attached — and `fired` guards nothing here, so a
      // caller that keys off a name sees each clip exactly once.
      onClipStart?.(names[0]);
    });
  }

  /** Loop a clip. Returns the entry so a caller can tune its speed. */
  /**
   * Does the chosen clip carry this event?
   *
   * Asked BEFORE it runs, because the answer decides whether the caller may
   * WAIT for it. Waiting on an event the rig does not have would hold the
   * golden cell back until the whole clip had finished.
   *
   * Read off the loaded skeleton, so a re-export that drops the event
   * degrades to the older timing by itself instead of breaking the feature.
   */
  _clipHasEvent(spine, candidates, eventName) {
    const name = this._resolve(spine, candidates);
    if (!name || !eventName) return false;
    const animation = spine?.skeleton?.data?.findAnimation?.(name);
    if (!animation?.timelines) return false;
    return animation.timelines.some(
      (timeline) =>
        Array.isArray(timeline?.events) &&
        timeline.events.some((event) => event?.data?.name === eventName),
    );
  }

  _playLoop(spine, candidates) {
    const name = this._resolve(spine, candidates);
    if (!name || !spine?.state) return false;
    return spine.state.setAnimation(0, name, true) || false;
  }

  /**
   * Play a clip once and PIN it on its last frame, for good.
   *
   * `trackEnd = Number.MAX_VALUE` is what stops Spine from disposing the entry
   * when it passes its duration. Without it the skeleton drops to its setup
   * pose the moment the clip is over — and for `myg` that pose is the golden
   * sign at full alpha, sitting on top of the track it was supposed to uncover.
   */
  _playAndHold(spine, candidates) {
    const name = this._resolve(spine, candidates);
    if (!name || !spine?.state) return null;
    const entry = spine.state.setAnimation(0, name, false);
    if (entry) entry.trackEnd = Number.MAX_VALUE;
    return entry;
  }

  /**
   * Celebrate this cell, exactly as any other winning symbol does.
   *
   * ── Why it is the FRAME that bounces ──
   *
   * A Golden cell is one picture assembled from three pieces: the `my` frame,
   * the golden lid inside its window, and the outcome inside that. `my.win`
   * keys `myst_main`, the bone every one of them hangs off, so the whole cell
   * moves together. Bouncing the outcome alone would leave it rattling inside a
   * frame standing still.
   *
   * The clip is the gamble scatter's, key for key — same 0.6667s, same
   * amplitude — so a coin and a scatter celebrating side by side move as one
   * thing. It also pins `symbol_placeholder` at identity scale, which is what
   * keeps the injected lid from inheriting a scale some earlier clip left.
   *
   * ── Its own track, because the lights must not stop ──
   *
   * Track 0 is running the light chase for the rest of the feature (see
   * frame.settle). Played there, the win would replace it and the cell would go
   * dark for the length of its own celebration — the opposite of celebrating.
   *
   * On a track of its own the win overrides only the bones it keys and the
   * chase keeps the lights. Same reasoning as the scene accents, and the same
   * reason `mul.distribute` and the collector's glow do not fight either.
   *
   * ── The SAME loop count every other symbol gets ──
   *
   * `win` is a single 0.6667s arc, and the display window is usually longer, so
   * the caller works out how many passes fit and hands that number to every
   * symbol on the line. Ignoring it here — playing exactly one arc — is why the
   * board's own scatter kept bouncing while the two inside the feature had long
   * since stopped: the same line, three scatters, two of them finished early.
   *
   * So the convention is the engine's, verbatim: 0 for ever, 1 once and held,
   * N passes then held. Held rather than ended, so a cell that finishes before
   * its line does stays put instead of dropping back mid-window.
   *
   * Holding is safe because the clip finishes AT REST — its last key returns
   * the body to y=0 — so a held last frame and no clip at all are the same
   * picture, and the light chase on track 0 is untouched either way.
   *
   * @param   {number} [targetLoops] 0 = for ever, 1 = once, N = N passes.
   * @returns {boolean} false when the rig has no `win`, so a caller can tell
   *          "not played" from "played and not noticed".
   */
  playWin(targetLoops = 1) {
    if (this.destroyed) return false;
    const name = this._resolve(this.frameSpine, this.config.frame?.win);
    if (!name || !this.frameSpine?.state) return false;

    const shouldLoop = targetLoops === 0 || targetLoops > 1;
    const entry = this.frameSpine.state.setAnimation(
      WIN_TRACK,
      name,
      shouldLoop,
    );
    if (!entry) return false;

    // Counted the way showWinAnimation counts them: let it loop, and clear the
    // flag on the pass that reaches the target so it holds the last frame.
    if (targetLoops > 1) {
      let completed = 0;
      entry.listener = {
        complete: () => {
          completed += 1;
          if (completed >= targetLoops) entry.loop = false;
        },
      };
    }
    return true;
  }

  /**
   * Show or park the whole cell.
   *
   * ── Why parking it is the same as "becoming a texture" ──
   *
   * The reel underneath already holds this cell's composite — frame, window and
   * outcome baked into one picture, painted at settle. It is the SAME picture
   * this spine is drawing live. So hiding the spine does not remove anything:
   * it hands the cell to the flat copy, which the dim overlay then covers like
   * every other symbol on the board.
   *
   * That is what a win cycle needs between lines. A live Golden cell draws on
   * the overlay layer, ABOVE the dim, so it stays lit through every other
   * line's turn no matter what the win data says.
   *
   * Everything hangs off the frame spine — the lid and the outcome are slot
   * objects inside its window — so one flag takes the whole cell.
   *
   * `autoUpdate` follows it: a parked cell must not keep ticking its light
   * chase for a picture nobody can see.
   *
   * @param {boolean} visible
   */
  setCellVisible(visible) {
    if (this.destroyed || !this.frameSpine) return;
    this.frameSpine.visible = !!visible;
    this.frameSpine.autoUpdate = !!visible;
  }

  /**
   * Stop celebrating and leave the cell exactly as it was.
   *
   * An empty animation rather than clearTrack: the win has the cell a few
   * pixels off its rest position when a line moves on, and clearing snaps it
   * back. Mixed out, it settles.
   */
  stopWin() {
    if (this.destroyed || !this.frameSpine?.state) return;
    this.frameSpine.state.addEmptyAnimation(WIN_TRACK, WIN_MIX_S, 0);
  }

  /** Resolve after `ms`, on the shared ticker so a destroy can cut it short. */
  /**
   * Fade a spine out over `ms`, then leave it hidden.
   *
   * The lid's own clip does not remove itself — it is authored to be a window
   * that STAYS, and here it has to leave. Done in code for the same reason
   * RevealSymbolController does it: alpha keys added to the clip are thrown
   * away by the next re-export, and this way one tween takes the doors, the
   * plate and everything else together.
   */
  /**
   * Hide a spine AND stop it solving.
   *
   * ── `visible = false` is only half of it ──
   *
   * It stops the spine being DRAWN, and that is all it does. `autoUpdate` is a
   * separate subscription: spine-pixi registers `internalUpdate` on
   * `Ticker.shared`, and that callback runs `state.update`, `state.apply`,
   * `skeleton.update` and `skeleton.updateWorldTransform` on EVERY frame with
   * no reference to `visible`, `renderable` or `worldAlpha`. An invisible
   * skeleton solves its whole hierarchy for nothing.
   *
   * It matters here more than anywhere else in the game. The only spine this
   * is called on is the LID, and the lid is gone for good once it has opened —
   * its doors blow off and the frame underneath owns the cell for the rest of
   * the feature. With fifteen mysteries open that is fifteen invisible
   * skeletons, 13 bones and 17 slots each, being solved every frame until the
   * next spin start hands them back to the pool.
   *
   * Handing them back EARLIER buys nothing on top of this: the pool's parked
   * state is exactly "hidden and asleep", so the frame cost is already zero
   * once this has run, and releasing early would only race
   * clearGoldenMysteries for the same object.
   *
   * See [[alpha-0001-is-not-hidden]] — same rule, third time it has bitten.
   *
   * @param {Object} spine
   */
  _sleep(spine) {
    if (!spine) return;
    spine.visible = false;
    spine.autoUpdate = false;
  }

  _fadeOut(spine, ms) {
    if (!spine) return Promise.resolve();
    if (!(ms > 0)) {
      spine.alpha = 0;
      this._sleep(spine);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const from = spine.alpha ?? 1;
      let elapsed = 0;
      const tick = () => {
        if (this.destroyed || !spine) {
          PIXI.Ticker.shared.remove(tick);
          resolve();
          return;
        }
        elapsed += PIXI.Ticker.shared.deltaMS;
        const t = Math.min(1, elapsed / ms);
        spine.alpha = from * (1 - t);
        if (t >= 1) {
          this._sleep(spine);
          PIXI.Ticker.shared.remove(tick);
          resolve();
        }
      };
      PIXI.Ticker.shared.add(tick);
    });
  }

  _wait(ms) {
    return new Promise((resolve) => {
      let remaining = ms;
      const tick = () => {
        if (this.destroyed) {
          PIXI.Ticker.shared.remove(tick);
          resolve();
          return;
        }
        remaining -= PIXI.Ticker.shared.deltaMS;
        if (remaining <= 0) {
          PIXI.Ticker.shared.remove(tick);
          resolve();
        }
      };
      PIXI.Ticker.shared.add(tick);
    });
  }

  /**
   * This cell's spin clock — the ONE thing here a player may cut short.
   *
   * A full board spins thirty of these at once and stops them 750ms apart, so
   * the wait is where nearly all of the feature's nine seconds live. Each tap
   * ends exactly ONE of them, and the registry always picks the lowest
   * `order` — which is this cell's `stopOrder`, the same wave the cascade was
   * going to follow anyway. Spamming therefore compresses the gaps without
   * reordering the board or changing what lands where.
   *
   * Everything after this is untouched. The track still lands through
   * stopOn(outcome), and the lid, the doors and the collect all play in full.
   *
   * @param {number} ms    How long it would have spun for.
   * @param {number} order This cell's place in the stop cascade.
   */
  _waitForSpin(ms, order, armAfterMs = 0) {
    return new Promise((resolve) => {
      let remaining = ms;
      let armIn = armAfterMs;
      const finish = () => {
        PIXI.Ticker.shared.remove(tick);
        releaseSkippableSpin(this._skipId);
        this._skipId = null;
        resolve();
      };
      const tick = () => {
        if (this.destroyed) {
          finish();
          return;
        }
        const delta = PIXI.Ticker.shared.deltaMS;
        remaining -= delta;
        // Not skippable until the track is actually VISIBLE — see the call
        // site. Until then the clock runs but the cell is off the registry, so
        // a press goes to whichever cell is already uncovered, or nowhere.
        if (this._skipId === null && armIn > 0) {
          armIn -= delta;
          if (armIn <= 0 && remaining > 0) {
            this._skipId = registerSkippableSpin(order, finish);
          }
        }
        if (remaining <= 0) finish();
      };
      // Never hold two entries. A second register without a release would
      // strand the first on the list, and a stranded entry swallows a press
      // without stopping anything.
      releaseSkippableSpin(this._skipId);
      this._skipId = null;
      if (armAfterMs <= 0) {
        this._skipId = registerSkippableSpin(order, finish);
      }
      PIXI.Ticker.shared.add(tick);
    });
  }

  // ── The track ────────────────────────────────────────────────────────────

  /**
   * Build the strip inside the golden layer's slot.
   *
   * @param {Array} items What the track spins. The round's own outcomes.
   */
  _injectStrip(items) {
    const stripCfg = this.config.strip;
    const slots = this.config.golden?.slotNames;
    this._slotName = this._resolveSlot(this.goldenSpine, slots);
    if (!this._slotName || !stripCfg) {
      console.warn("[GoldenMystery] No injection slot found:", slots);
      return false;
    }

    this.holder = new PIXI.Container();
    this.holder.name = "golden_mystery_holder";
    this.fit = new PIXI.Container();
    this.fit.name = "golden_mystery_fit";
    this.holder.addChild(this.fit);

    const stripItems = items?.length ? items : stripCfg.items;

    // ── Rasterise every number this strip can show, before it moves ──
    //
    // The cells draw their value as a SPRITE onto a cached texture, and the one
    // rasterisation per label has to happen somewhere. Here is the right
    // somewhere: the board is still being built and no frame depends on it.
    // Left to the strip, the first cell to wrap onto an unseen value would pay
    // for it mid-motion — which is the stutter this whole change is removing.
    //
    // Idempotent, so fifteen cells warming the same pool cost one pass each
    // over a short list and nothing else.
    stripCfg.warmLabels?.(stripItems, this.cellHeight);

    this.strip = new SymbolStripSpinner({
      parent: this.fit,
      cellWidth: this.cellWidth,
      cellHeight: this.cellHeight,
      visibleCount: 1,
      items: stripItems,
      createCell: stripCfg.createCell,
      renderCell: stripCfg.renderCell,
      speedMs: stripCfg.speedMs,
      accelMs: stripCfg.accelMs,
      stopMs: stripCfg.stopMs,
      overshoot: stripCfg.overshoot,
      windUpDistance: stripCfg.windUpDistance,
      windUpMs: stripCfg.windUpMs,
    });

    // A PIXI mask, because Spine's own clipping does not contain slot objects:
    // an object added with addSlotObject is a child positioned by the slot's
    // bone, not part of the skeleton's batch. Same note as
    // RevealSymbolController's.
    const g = new PIXI.Graphics();
    g.beginFill(0xffffff);
    g.drawRect(
      -this.cellWidth / 2,
      -this.cellHeight / 2,
      this.cellWidth,
      this.cellHeight,
    );
    g.endFill();
    this.fit.addChild(g);
    this.fit.mask = g;
    this.maskGraphic = g;

    // ── A slot holds exactly ONE object ──
    //
    // spine-pixi keeps `slotsObject` as a Map keyed by slot, so a second
    // addSlotObject on the same slot REPLACES the first. With one host the
    // frame holder and the strip holder both want
    // `attachment_slot_symbol`, and the strip would evict the frame holder —
    // taking the effects container with it.
    //
    // So when the host is its own golden rig the strip is parented INSIDE the
    // frame's fit, which is already that slot's one object. Same pixels, same
    // bone, same mask — one fewer slot object.
    if (this.goldenSpine === this.frameSpine && this.frameFit) {
      this.frameFit.addChild(this.holder);
    } else {
      this.goldenSpine.addSlotObject(this._slotName, this.holder);
    }
    this._applyFit();
    return true;
  }

  /** First slot name in `candidates` that exists on `spine`. */
  _resolveSlot(spine, candidates) {
    const skeleton = spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates || []) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /**
   * Make one unit below `fit` equal one cell pixel.
   *
   * The holder inherits the placeholder bone's world scale (every symbol
   * skeleton here is built under a `scale` bone) and the host's own PIXI scale.
   * Dividing both out leaves the fit at world scale 1, which is the unit the
   * strip's cells are written in. Read from the skeleton rather than hardcoded,
   * so a re-export cannot silently break it.
   */
  _applyFit() {
    if (!this.fit || !this.goldenSpine) return;

    // ── Do NOT cancel a bone chain that is already cancelled ──
    //
    // With one host, `frameFit` and this `fit` hang off the SAME spine and the
    // SAME slot, and `frameFit` is this fit's parent. _applyFrameFit has
    // already divided by `boneScale * spineScale`; doing it again here would
    // scale the strip by 1 / total squared and shrink it to a speck inside a
    // correctly sized window.
    if (this.goldenSpine === this.frameSpine && this.frameFit) {
      this.fit.scale.set(1);
      return;
    }

    const slot = this.goldenSpine.skeleton?.findSlot?.(this._slotName);
    const bone = slot?.bone;
    const boneScale = bone ? Math.hypot(bone.a, bone.c) : 1;
    const spineScale = this.goldenSpine.scale?.x || 1;
    const total = boneScale * spineScale;
    this.fit.scale.set(total ? 1 / total : 1);
  }

  // ── The sequence ─────────────────────────────────────────────────────────

  /**
   * Run the whole cell: build-up, doors, lid, track, landing.
   *
   * @param   {Object} p
   * @param   {{symbolId:number,value:number}} p.outcome What this cell landed
   *          on. Comes from the server — this class never decides it.
   * @param   {Array}  p.items   The pool the track spins.
   * @param   {string} p.anticipationClip Which build-up plays before the doors
   *          open. The drama decides it, per round — one clip, once.
   * @param   {number} p.stopOrder This cell's place in the stopping order —
   *          0 stops first. Every cell starts together; only the stop cascades.
   * @returns {Promise<void>} resolves once the track has settled.
   */
  async play({
    outcome,
    items,
    anticipationClip = null,
    stopOrder = 0,
    onRevealed = null,
  } = {}) {
    if (this.destroyed) return;
    const frame = this.config.frame || {};
    const lid = this.config.lid || {};

    // ── TWO LAYERS ──
    //
    //   lid    the ordinary mystery, `my`, on top. Build-up, doors, gone.
    //   frame  the golden frame below it, lit and waiting, with `myg` inside.
    //
    // The frame used to do both jobs, so a golden cell opened with the OLD
    // rig's doors while every other mystery used the new ones. Now the same
    // doors open everywhere, and what is behind them is what differs.
    //
    // `useLid` is false only when the pool could not give us a lid spine. The
    // old single-layer path is kept for that case rather than opening onto
    // nothing.
    const useLid = !!this.lidSpine;

    // 1. The frame, ready UNDER the lid: lights chasing, doors out of the way.
    //
    //    `anticipation_spin` HIDES the door slots rather than moving them, so
    //    this alone is the open, lit frame — no separate opening pose needed.
    //    Started now, not later, because the lid is opaque over it: by the time
    //    anything is uncovered the chase is already running, instead of
    //    switching on in front of the player.
    if (useLid) {
      // The golden sign goes in behind the lid, for the same reason it always
      // went in behind closed doors: nothing may be seen arriving in a window
      // that is already open.
      if (!this._injectGolden()) return;
      // ── HIDDEN until the lid says so ──
      //
      // Built now so it is ready and its chase is already running, but not
      // shown. The lid's plate starts thinning at t=0.1 while the event that
      // uncovers an ordinary symbol is at t=0.2, so left visible the golden
      // frame appeared EARLIER than every other reveal on the board. Same
      // gesture, same moment — see the event below.
      // ── Hidden AND asleep, and the second half is not decoration ──
      //
      // The frame waits here for the whole build-up — the lid's anticipation,
      // its doors, its fade — which is seconds, not frames. `visible = false`
      // stops it being drawn and leaves `autoUpdate` alone, so its skeleton
      // went on being solved the entire time.
      //
      // At one cell that is invisible. The perf probe caught it at thirty:
      // `19b/24s x30` in the HIDDEN column, 570 bones and 720 slots solved
      // every frame for a picture nobody could see, right through the busiest
      // moment the feature has. Woken again where it is shown, below.
      this.frameSpine.visible = false;
      this.frameSpine.autoUpdate = false;
      this._frameSleptAt = performance.now();

      // ── The chase waits for the reveal, on a single host ──
      //
      // Two spines could do both at once: the frame chased while `myg` burned
      // its sign away on top. ONE spine cannot — `spin` and `reveal` are the
      // same track, and starting the chase here would override the reveal and
      // the sign would never fade.
      //
      // So the host simply sits at SETUP, which is the gold sign at rest — the
      // closed state the lid must open onto. The chase starts when the reveal
      // has finished; see step 6.
      if (!this._singleHost) {
        this._playLoop(this.frameSpine, frame.spinning);
      }
    }

    // 2. Build-up — on the LID when there is one, on the frame when there is
    //    not. ONE clip, once, named by the round.
    //
    //    It used to loop a single short pulse a counted number of times. The
    //    rig now carries the build-up as two authored clips — short and long —
    //    and each is a whole performance, so repeating one cuts across its own
    //    arc. Same change, same reason, as the ordinary reveal's.
    //    Skippable, in the SAME group as the ordinary mystery's build-up: a
    //    board can hold both at once and they play together, so one press has
    //    to end all of them. The golden cell's later phases — the lid, the
    //    strip's own wait — keep their own separate skips.
    const buildUpSpine = useLid ? this.lidSpine : this.frameSpine;
    const buildUpClips = useLid ? lid.anticipation : frame.anticipation;
    // The same build-up sound the ordinary mystery gets, resolved from the same
    // table: a board can hold both kinds and they play together, so they must
    // sound like one event. Once per frame, and the gate lives in SoundManager
    // so it is shared with RevealSymbolController rather than per class.
    soundManager.playOncePerFrame(mysteryDramaSound(anticipationClip));
    await this._playOnce(
      buildUpSpine,
      anticipationClip
        ? [anticipationClip, ...(buildUpClips || [])]
        : buildUpClips,
      { group: MYSTERY_SKIP_GROUP, order: 0 },
    );

    // ── The build-up is CUT here, however that await ended ──
    //
    // It is 1.5s or 3.0s and the player can SKIP it: the skip stops the CLIP
    // and the sequence walks straight on to the doors, so without this the
    // anticipation carries on ringing over a cell that has already opened,
    // still building to something that has happened.
    //
    // After the await rather than inside the skip, so the one line covers
    // every way the build-up can end — run out, skipped, or the cell
    // destroyed under it.
    //
    // Both names, because the round picks the clip and this code only has the
    // one it was handed; fadeOutOneShot is a no-op on a sound that is not
    // playing. The ordinary mystery does the same thing through `cutSound` on
    // its reveal config — same rule, two controllers.
    soundManager.fadeOutOneShot(SOUND_NAME.mystery_reveal_fast, SFX_CUT_FADE_MS);
    soundManager.fadeOutOneShot(SOUND_NAME.mystery_reveal_slow, SFX_CUT_FADE_MS);

    if (this.destroyed) return;

    if (!useLid) {
      // The old single-layer path, unchanged: the golden sign goes in behind
      // the frame's own closed doors, and the frame opens them itself.
      if (!this._injectGolden()) return;
      await this._playOnce(this.frameSpine, frame.open);
      if (this.destroyed) return;
    } else {
      // 3. The lid opens and leaves, uncovering the frame that was there all
      //    along. The fade runs UNDER the opening doors and lands with them —
      //    `fadeOutEndMs` is when the lid must be GONE, not when it starts to
      //    go. Same contract as the ordinary reveal; see symbolData[9].reveal.
      // ── The frame appears on the SAME event as any revealed symbol ──
      //
      // `show_symbol`, placed by the animator in the open clip. An ordinary
      // reveal swaps the reel texture there; a golden cell shows the frame
      // there. One moment, one rule, whatever is underneath.
      //
      // No event in the rig means no way to wait for one, so it is uncovered
      // at once — which is what it did before this existed.
      const showFrame = () => {
        if (this.destroyed || !this.frameSpine) return;
        // ── Woken, and FAST-FORWARDED to where it would have been ──
        //
        // Sleeping it is not free of consequence. The chase is started on this
        // spine while it is still hidden, and the note above says why: the lid
        // is opaque over it, so by the time anything is uncovered the chase is
        // already running "instead of switching on in front of the player".
        // `myg.spin` opens with every bamboo light at alpha 0, so a skeleton
        // resumed at track time zero would do exactly that — switch on.
        //
        // One catch-up call fixes it. `Spine.update(seconds)` is the manual
        // form of the tick and costs the same as one frame however long the gap
        // was: it advances the track, applies it and solves the hierarchy once.
        // So the pose the player is uncovered onto is the pose the chase would
        // have reached, and none of the frames in between were ever computed.
        this.frameSpine.autoUpdate = true;
        if (this._frameSleptAt) {
          const asleepS = (performance.now() - this._frameSleptAt) / 1000;
          this._frameSleptAt = null;
          try {
            if (asleepS > 0) this.frameSpine.update(asleepS);
          } catch {
            // A rig that cannot be advanced still has to be shown.
          }
        }
        this.frameSpine.visible = true;
      };
      const lidEvent = lid.openingEvent;
      const hasLidEvent =
        !!lidEvent && this._clipHasEvent(this.lidSpine, lid.open, lidEvent);
      if (!hasLidEvent) showFrame();

      const opening = this._playOnce(
        this.lidSpine,
        lid.open,
        null,
        hasLidEvent ? { name: lidEvent, fire: showFrame } : null,
      );
      // However the clip ends — run out, replaced, cut — the frame must be
      // showing. A no-op once the event has run.
      opening.then(showFrame);
      const fadeMs = lid.fadeOutMs ?? 0;
      if (fadeMs > 0) {
        const goneBy = lid.fadeOutEndMs ?? 0;
        const startAt = goneBy > 0 ? Math.max(0, goneBy - fadeMs) : 0;
        if (goneBy > 0) {
          if (startAt > 0) await this._wait(startAt);
        } else {
          await opening;
        }
        if (this.destroyed) return;
        await this._fadeOut(this.lidSpine, fadeMs);
      } else {
        await opening;
        if (this.destroyed) return;
        this._fadeOut(this.lidSpine, 0);
      }
      if (this.destroyed) return;
    }

    // ── The cell is now visibly GOLDEN, and not one frame sooner ──
    //
    // Everything before this point looks like an ordinary mystery: the same
    // frame, the same build-up, the same doors. The golden sign is only on
    // screen once step 3 has parted them.
    //
    // So this is the earliest honest moment to tell the scene. Announcing it at
    // the START of the sequence would light the reels while the doors were
    // still shut, which tells the player what is behind them before they are
    // shown — the one thing the build-up exists to withhold.
    onRevealed?.();

    // 4. The frame's light chase, looping for the rest of the feature.
    //
    //    Only when the lid path did not already start it in step 1. Restarting
    //    the loop here would jump the chase back to its first frame at the one
    //    moment the player is finally looking at it.
    if (!useLid) this._playLoop(this.frameSpine, frame.spinning);

    // 5. Build the track and START it, BEFORE the lid comes off. `reveal` keys
    //    the lid's own slot from alpha 0 up to full on its last frame, so the
    //    track is invisible until the boom — and by then it is already at
    //    speed. Start it after instead and the player watches a track begin
    //    from nothing, which is the one thing the boom exists to hide.
    if (!this._injectStrip(items)) return;
    this.strip.start();
    // The spin's SOUND does not start here — see step 6. The strip still does,
    // because it has to be at speed before the lid lifts off it.

    // 6. The lid: once, then pinned on its last frame for ever.
    // ── The golden boom, and why it is gated per frame ──
    //
    // Every golden cell on the board reaches this line in the SAME frame: the
    // stagger is added at step 7 and nowhere earlier, which is what makes the
    // cells start as one. Ungated, ten cells would stack ten copies of the same
    // 1.5s clip on top of each other.
    //
    // Same gate as the build-up above, and it lives in SoundManager so the two
    // share it rather than each keeping a Set of its own.
    soundManager.playOncePerFrame(SOUND_NAME.golden_mystery_reveal);

    const lidEntry = this._playAndHold(
      this.goldenSpine,
      this.config.golden?.reveal,
    );

    // 7. Let it run, then land on what the server sent.
    //
    // The stagger is added HERE and nowhere earlier, which is what makes the
    // cells start as one and stop as a wave. Putting it on the build-up or the
    // doors instead would open ten frames at ten different times, and the board
    // would look like ten separate events rather than one.
    const spinMs =
      (this.config.spinDurationMs ?? 0) +
      stopOrder * (this.config.stopStaggerMs ?? 0);
    // ── Not skippable while the lid is still over it ──
    //
    // `golden.reveal` is a ~1s clip that covers the track and then holds its
    // last frame, so the first second of every cell's spin happens behind it.
    // Registering from the start let the player stop cells they had not been
    // shown yet: five could be taken down before the board had finished
    // opening, which reads as presses going nowhere.
    //
    // Read off the clip rather than hardcoded, so a re-export that changes the
    // lid's length moves this with it.
    const lidMs = (lidEntry?.animation?.duration ?? 0) * 1000;

    // ── The spin's SOUND waits for the reveal to finish ──
    //
    // `lidMs` is the reveal clip's own length, read off the clip. The strip has
    // been turning since step 5 — a track that begins from nothing as the lid
    // lifts is what the boom exists to hide — but nothing is HEARD until the
    // lid is off it, so the boom and the loop are one after the other rather
    // than on top of each other. Vladimir, 2026-09-22.
    //
    // Ref-counted, so every cell asks and only the first starts it; they all
    // wait the same length and still come in together.
    this._spinSoundArmed = true;
    this._wait(lidMs).then(() => {
      // Disarmed by _stopSpinSound — a spin SKIPPED inside this window would
      // otherwise take a reference a moment after the code that gives it back
      // has already run, and the loop would play for the rest of the session.
      if (this.destroyed || !this._spinSoundArmed) return;
      this._spinSoundArmed = false;
      this._startSpinSound();
    });

    // ── Reveal first, THEN the chase ──
    //
    // Vladimir's order: the lid opens onto the covered sign, the sign burns
    // away, and only then does the light chase run under the turning strip.
    // On a single host these are the same track, so the chase has to wait for
    // the reveal to finish rather than run beside it. Scheduled off the clip's
    // own duration, so a re-export that lengthens the reveal moves this too.
    if (this._singleHost && useLid) {
      const startChase = () => {
        if (this.destroyed) return;
        this._playLoop(this.frameSpine, frame.spinning);
      };
      if (lidMs > 0) {
        this._wait(lidMs).then(startChase);
      } else {
        startChase();
      }
    }

    if (spinMs > 0) await this._waitForSpin(spinMs, stopOrder, lidMs);
    if (this.destroyed) return;

    await this.strip.stopOn(outcome);
    // The coin has landed — this cell's share of the loop is done. The sound
    // itself only stops once every other cell has said the same.
    this._stopSpinSound();
    if (this.destroyed) return;

    // 8. The frame keeps chasing, on the same looped clip it span under — see
    //    frame.settle. The track stays where it landed; what happens to the
    //    landed cell next is the feature's business, not this cell's.
    this._playLoop(this.frameSpine, frame.settle);

    // 9. The symbol bounces, exactly as it would on a reel.
    await this.playLandingBounce(outcome);
  }

  /**
   * The landed symbol's arrival clip — the same one the reels play.
   *
   * ── Why every cell, and not just the ones that act ──
   *
   * A Golden cell IS a reel: it spins a strip and stops on a symbol. Every
   * symbol on the real reels plays its landing clip before anything else, and
   * until now none of these did — the track stopped and left a flat picture
   * sitting there. The multiplier and the collector looked alive only because
   * the loop went on to ask them to DO something, and `playCellSpine` puts
   * `bounce` in front of whatever it is asked for.
   *
   * All six spines a cell can land on carry `bounce`: `c`, `cl`, `mul`, `sc`,
   * `scg` and `bs`. So this is one rule with no exceptions to remember.
   *
   * ── Why it does not flash ──
   *
   * The same argument playCollect makes for `cl`, checked against all six: every
   * key `bounce` sets at t=0 is on an EFFECT slot at zero alpha — a glow, an
   * eclipse, an energy bar. Not one of them touches the symbol's own art. So
   * frame 0 is the picture the flat cell is already showing, and the handover is
   * invisible in both directions.
   *
   * Skips itself silently when the outcome has no spine (an unknown multiplier
   * value) or the rig has no `bounce`. A missing animation costs this beat, never
   * the feature — which holds the game's UI lock.
   *
   * @param {{symbolId:number,value:number}} outcome What the track stopped on.
   */
  async playLandingBounce(outcome) {
    if (this.destroyed) return;

    // ── The sound goes first, and it is NOT conditional on the clip ──
    //
    // A rig with no `bounce`, or an outcome with no spine, still landed. The
    // reels order it the same way: useLandingAnimations plays the landing sound
    // before it decides between the landing clip, the idle and the bare texture.
    playLandingSoundOncePerFrame(this.config?.landingSound?.(outcome));

    const resolve = this.config?.landingSpine;
    const target = typeof resolve === "function" ? resolve(outcome) : null;
    if (!target?.spineName) {
      return;
    }

    await this.playCellSpine({
      spineName: target.spineName,
      clip: "bounce",
      skin: target.skin ?? null,
      // The live spine IS the symbol for this beat, so the flat one steps aside
      // — one frame late, as the reels do it.
      hideStrip: true,
      // And it stands exactly where the flat picture stood: inside the golden
      // window, masked to the cell, at the track's own scale.
      inCell: true,
      // ── With its number on it, and ON the spine ──
      //
      // A live spine carries no number and the flat picture it replaced did, so
      // without this a coin bounces blank — a symbol lying about its own value
      // for half a second.
      //
      // Worn by the SPINE rather than drawn beside it: `c.bounce` keys the
      // `value` bone, so a number in the cell container would sit still while
      // the coin springs. See wearCellNumber for the counter-scale that keeps
      // its resting size identical to the flat one.
      decorate: (parent, spine) =>
        this.config?.wearCellNumber?.({
          spine,
          item: outcome,
          cellWidth: this.cellWidth,
          cellHeight: this.cellHeight,
        }) ?? null,
    });
  }

  /**
   * Resolve at the end of the current loop iteration.
   *
   * Spine fires `complete` at every loop boundary, so a looping track announces
   * its own seam — the one place a pose can change without a jolt.
   */
  _waitForLoopCycle(spine) {
    const state = spine?.state;
    const entry = state?.tracks?.[0];
    if (!state || !entry?.loop) return Promise.resolve();

    return new Promise((resolve) => {
      const settle = this._settler(resolve);
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        state.removeListener(listener);
        this._listeners = this._listeners.filter((l) => l.listener !== listener);
        settle();
      };
      const listener = {
        complete: (completed) => {
          if (completed === entry) finish();
        },
        end: (ended) => {
          if (ended === entry) finish();
        },
      };
      this._listeners.push({ spine, listener });
      state.addListener(listener);
    });
  }

  // ── The loop: what a settled cell can be asked to do ─────────────────────
  //
  // After the track lands, the cell is a flat picture inside the frame. The
  // multiplier and collect loop then asks it to change: to show a bigger number,
  // to fire, to be swallowed, or to spin again. Each of these is deliberately
  // small and deliberately safe — a missing spine skips its step rather than
  // stopping the feature, because the feature holds the game's UI lock and a
  // stall there costs the session, not the round.

  /** What this cell is currently showing. Set by the loop as it changes. */
  setItem(item) {
    if (this.destroyed || !this.strip) return;
    this.strip.setItem(item);
  }

  /** Hide or show the landed picture — used while a live spine acts in its place. */
  _setStripVisible(visible) {
    if (visible) this._cancelPendingHide();
    if (this.strip?.container) this.strip.container.visible = visible;
  }

  /**
   * Hide the landed picture ONE FRAME LATE, which is what the reels do.
   *
   * `useLandingAnimations` starts the spine and then hides the static texture
   * inside a `requestAnimationFrame`, and says why: it "allows spine GPU
   * textures to upload before texture disappears. The 1-frame overlap is
   * invisible since both show the same symbol image."
   *
   * Hiding in the SAME frame the spine is shown is a bet that the spine's first
   * frame is on screen already. When it is not — an atlas page not yet resident,
   * a world transform not yet computed — the player gets one frame of nothing,
   * and one frame of nothing is exactly what a flash looks like.
   *
   * The overlap is safe here for the same reason it is safe on the reels: the
   * effects container is added AFTER the lid, so the live spine draws OVER the
   * flat cell rather than under it. For one frame the same picture is drawn
   * twice, in the same place, at the same size.
   */
  _hideStripNextFrame() {
    if (this._pendingHide !== null && this._pendingHide !== undefined) return;
    this._pendingHide = requestAnimationFrame(() => {
      this._pendingHide = null;
      if (this.destroyed) return;
      if (this.strip?.container) this.strip.container.visible = false;
    });
  }

  _cancelPendingHide() {
    if (this._pendingHide === null || this._pendingHide === undefined) return;
    cancelAnimationFrame(this._pendingHide);
    this._pendingHide = null;
  }

  // ── Where this cell is, for effects that travel BETWEEN cells ─────────────
  //
  // A multiplier does not act in place: it reaches across the board and hits
  // every coin standing on it. That effect belongs to neither end of the
  // journey, so it cannot be drawn inside either cell — a cell's container is
  // positioned and scaled for that cell, and anything drawn in it is sorted
  // with it, so a trail leaving one would be covered by the next symbol it
  // flies over.
  //
  // It needs a container both cells hang off and two points measured in it.
  // These two methods are that, and nothing more: this class does not know what
  // a trail is.

  /**
   * The centre of this cell on the GRID, in the trail layer's space.
   *
   * ── Measured from the cell, never from what is drawn in it ──
   *
   * This used to read `effects.toGlobal(0,0)`, on the reasoning that the frame's
   * window is where the symbol art actually sits. That reasoning was wrong in a
   * way that only moves: `effects` lives inside the mystery frame's
   * `attachment_slot_symbol`, and a slot object's transform is rewritten every
   * frame from the skeleton. `bounce` translates `myst_main`, the light chase
   * loops, the doors part — so "the centre of the symbol" drifted with whatever
   * the frame was doing, and a bolt aimed at it landed wherever that bone
   * happened to be on the frame it was measured.
   *
   * The overlay symbol container is positioned from the LAYOUT and nothing
   * animates it, through resizes included. That is the grid. Same conclusion
   * Pot Collection reached for its own bolts — see Games/Shared/symbolGrid.
   *
   * @param   {PIXI.Container} layer The space to measure in — the trail layer
   *          the effect is drawn into, which is GameView's and not this cell's.
   * @returns {{x:number,y:number}|null}
   */
  gridCenter(layer) {
    return cellCenterIn(layer, this.cellContainer);
  }


  /**
   * Play a live spine over the cell, once, and resolve when it ends.
   *
   * @param {Object}   p
   * @param {string}   p.spineName
   * @param {string}   p.clip
   * @param {string}   [p.skin]
   * @param {boolean}  [p.hideStrip]  Cover the flat picture while it plays —
   *        true when the spine IS the symbol (a multiplier firing), false when
   *        it is an effect on top of it (the upgrade burst).
   * @param {string}   [p.onEventName] A Spine event to listen for.
   * @param {Function} [p.onEvent]     Called when that event fires. This is how
   *        a value change lands on the exact frame the animator chose — see the
   *        `upgrade` event at 0.2667.
   * @param {boolean}  [p.inCell]   Play inside the MASKED window — a clip that
   *        is purely the symbol and must never cross the cell's edge.
   * @param {boolean}  [p.inWindow] Play inside the golden window but UNMASKED —
   *        a symbol whose clip carries a burst. See the note on the three homes.
   * @param {Function} [p.decorate] Build something worn by the spine, e.g. a
   *        coin's number. Rebuilt on resize.
   * @returns {Promise<void>}
   */
  async playCellSpine({
    spineName,
    clip,
    skin = null,
    hideStrip = false,
    onEventName = null,
    onEvent = null,
    inCell = false,
    inWindow = false,
    decorate = null,
  }) {
    // ── THREE homes, and they are not interchangeable ──
    //
    //   aboveEffects  the default. A child of the OVERLAY's cell container at
    //                 zIndex 20 — over the whole cell and above the frame. Right
    //                 for a multiplier firing or a magnet striking, which are
    //                 drawn to spill past the cell and to sit over everything.
    //
    //   effects       `inWindow`. A child of `frameFit`, so it is INSIDE the
    //                 golden window in the frame's own space — and unmasked. For
    //                 a symbol whose clip has a burst: the symbol stays in the
    //                 window where its picture was, and the burst is still
    //                 allowed to spill rather than being cut at the edge.
    //
    //   fit           `inCell`. The masked container the track's own cells live
    //                 in. The strictest, and right for a clip that is purely the
    //                 symbol — a landing bounce — where nothing should ever
    //                 cross the window's edge.
    //
    // Getting this wrong is visible, not subtle: on `aboveEffects` a symbol
    // draws at full cell size over the frame and reads as climbing OUT of the
    // mystery to play its clip.
    const parent = inCell
      ? this.fit
      : inWindow
        ? this.effects || this.aboveEffects
        : this.aboveEffects || this.effects;

    if (
      this.destroyed ||
      !parent ||
      typeof this.makeFeatureSpine !== "function"
    ) {
      onEvent?.();
      return;
    }

    const spine = this.makeFeatureSpine({
      parent,
      spineName,
      skin,
      cellHeight: this.cellHeight,
    });
    if (!spine) {
      // No spine, no animation — but the state change it carries must still
      // happen, or the board and the payout part company.
      onEvent?.();
      return;
    }

    // One frame late, exactly as the reels do it — see _hideStripNextFrame.
    if (hideStrip) this._hideStripNextFrame();

    // ── Held so a RESIZE can find it ──
    //
    // Everything in this class that owns a container is re-fitted in resize(),
    // and this spine was the one exception: it was a local, so a resize while it
    // played left it at the old cell's scale and — worse — left the number it
    // wears rasterised at the old size and counter-scaled against the old spine
    // scale. That is the exact fault resizeAnimations was fixed for once
    // already, one layer further in.
    this._live = { spine, parent, decorate, worn: null };

    // ── The clip ALONE — `bounce` is no longer prepended ──
    //
    // It used to be, as the bridge from the flat landed picture to the animated
    // state: the picture is captured at the resting pose, and a clip whose first
    // frame is not that pose starts on something the texture was never showing.
    //
    // The landing now plays `bounce` for every cell as it lands, so prepending
    // it here played the arrival a second time — the jolt [arrival-twice]
    // describes, reached from the other direction.
    //
    // Dropping it is safe because the premise was checked rather than assumed.
    // Across all six rigs, every key `bounce` and every action clip set at t=0
    // is on an EFFECT slot at zero alpha — a glow, an eclipse, an energy bar.
    // None of them touch the symbol's own art. So the resting picture, the first
    // frame of `bounce`, its last frame and the first frame of `distribute` or
    // `activation` are all the same picture, and every handover between them is
    // invisible. That is also what this file already claimed one comment down:
    // "bounce's last frame IS activation's first".
    const clips = [clip];

    // ── Anything the spine has to WEAR, added after it ──
    //
    // After, because draw order here is child order: added before the spine, the
    // number would be painted under it. Torn down in the same breath as the
    // spine, so nothing it drew can outlive the clip it belonged to.
    try {
      this._live.worn = decorate?.(parent, spine) ?? null;
    } catch (error) {
      // A number that cannot be built must not cost the animation — but it is
      // said out loud, because a coin standing there with no value on it is a
      // symbol lying about what it pays.
      console.error("[GoldenMystery] cell decoration failed:", error);
    }

    await this._playSequence(spine, clips, {
      onEventName,
      onEvent,
    });

    // Read off `_live` rather than a local: a resize during the clip REBUILDS
    // what is worn, so the local would be a container that was already thrown
    // away and the new one would be left behind.
    try {
      this._live?.worn?.destroy({ children: true });
    } catch {
      // already gone
    }
    this._live = null;

    if (hideStrip) this._setStripVisible(true);
    try {
      spine.destroy();
    } catch {
      // already gone
    }
  }

  /**
   * The collector's sequence: bounce, activate, done.
   *
   * ── Three clips became two, and the number stopped flickering ──
   *
   * `cl` carries five clips and only two of them belong in this moment.
   * `activation` is the collect — the ONLY clip that keys
   * `txt_slot_collector_value`, fading the number in between 0.333 and 0.433
   * while the `value` bone pops it. `bounce` is the arrival before it.
   *
   * The idle and the end clip were both in here and are not any more. They made
   * the sequence long and, worse, they made the number BLINK: neither of them
   * keys that slot, so starting either one reverted it to its setup pose and the
   * value disappeared. Every animation change took it away and put it back.
   *
   * ── The number is OURS, not the skeleton's ──
   *
   * So it is not injected into the slot any more. The live spine plays its two
   * clips and comes off, and the flat cell underneath — which already draws the
   * collector with its value, in the collector's own red-on-white style — is
   * what is left standing. That IS the static state, so there is nothing to hold
   * and nothing that can revert it.
   *
   * It costs the pop the `value` bone would have given the number. It buys a
   * number that appears once and never moves again, and it removes the bone
   * rotation that was turning it on its side.
   *
   * @param   {number} total What it collected. Computed by goldenMysteryScript.
   * @returns {Promise<void>} resolves when the cell is settled and static.
   */
  async playCollect(total, { onActivation = null } = {}) {
    const settle = (value) =>
      this.setItem({ symbolId: COLLECT_SYMBOL_ID, value });

    // Whoever settles this cell first wins. Normally that is the end of the
    // sequence; on a rig with no spine, or a clip that resolves instantly,
    // `showCollectedTotal` gets there first — and then the tail must not settle
    // it a second time or tear down a spine that is already gone.
    this._collectSettled = false;

    if (this.destroyed) return;
    if (!this.effects || typeof this.makeFeatureSpine !== "function") {
      // Nothing to play, but the value still has to land or the board and the
      // payout part company. The FULL total, never held: nothing is coming to
      // reveal it.
      settle(total);
      return;
    }

    const spine = this.makeFeatureSpine({
      // Above the frame, with the other live spines — see _injectGolden.
      parent: this.aboveEffects || this.effects,
      spineName: "cl",
      cellHeight: this.cellHeight,
    });
    if (!spine) {
      settle(total);
      return;
    }

    this._collectSpine = spine;
    // The live spine IS the collector for these two clips, so the flat one steps
    // aside rather than showing through it — one frame late, as the reels do.
    this._hideStripNextFrame();

    // ── `activation` ALONE ──
    //
    // `bounce` was here for a long argument's worth of good reasons: every reel
    // symbol plays its landing clip first, and this cell is a reel. That is
    // still true — it is just no longer THIS call's job. playLandingBounce plays
    // it for every cell the moment the track stops, the magnet included, so
    // playing it again here is the arrival happening twice.
    //
    // `cl.activation` can open on its own: at t=0 it keys only effect bones and
    // the value bone, whose slot is transparent there, so its first frame is the
    // resting picture the flat cell is already showing.
    //
    // ── `activation` is the moment the coins fire ──
    //
    // Not the landing. The strikes belong to the clip that says the magnet is
    // WORKING, and they cannot be scheduled from outside — a caller awaiting the
    // sequence has already missed the handover. Hence onClipStart, which now
    // fires as soon as the clip starts rather than 500ms into a bounce.
    const activationName = this._resolve(spine, ["activation"]);
    await this._playSequence(spine, ["activation"], {
      onClipStart: (name) => {
        if (name !== activationName) return;
        // On the SAME cue as the volley, not beside it. Both hang off
        // `activation` starting, so the strikes and the sound leave the magnet
        // together — scheduling this anywhere else would drift against the one
        // thing it is describing.
        //
        // Per frame, because two collectors can drop in one iteration and they
        // activate together; that is one event, not two.
        soundManager.playOncePerFrame(SOUND_NAME.collect_activate);
        onActivation?.();
      },
    });

    // Back to static — the flat cell, holding its total. By now the live number
    // has usually been showing it for a while (see showCollectedTotal); the two
    // are the same picture, so the handover is invisible.
    if (!this._collectSettled) {
      this._collectSettled = true;
      settle(total);
      this._teardownCollect();
    }
  }

  /**
   * The strikes have landed — show the total NOW, without touching the clip.
   *
   * ── Nothing is interrupted ──
   *
   * An earlier version ended `activation` here on the reasoning that the impact
   * is the payoff. It is, but cutting the clip made the magnet's own animation
   * stop dead the moment the bolts arrived, which reads as a fault rather than a
   * flourish. `activation` now runs to its end, every time.
   *
   * So the number is drawn OVER the live spine instead — same style, same size,
   * same offset as the flat cell's, so when the sequence ends and the flat cell
   * takes over there is nothing to see at the handover. See
   * buildCollectorNumber.
   *
   * Idempotent. If the sequence has already settled — a very short clip, or no
   * spine at all — the flat cell is simply given the value.
   *
   * @param {number} total
   */
  showCollectedTotal(total) {
    if (this.destroyed) return;

    if (this._collectSettled || !this._collectSpine) {
      this._collectSettled = true;
      this.setItem({ symbolId: COLLECT_SYMBOL_ID, value: total });
      return;
    }

    if (this._collectNumber) return;
    // Kept so a resize can rebuild the number rather than stretch it — see
    // resize().
    this._collectTotal = total;
    // Added AFTER the spine into the same container, so it draws on top of it.
    this._collectNumber =
      this.config?.buildCollectorNumber?.({
        parent: this.aboveEffects || this.effects,
        value: total,
        cellWidth: this.cellWidth,
        cellHeight: this.cellHeight,
      }) ?? null;
  }

  /** Drop the live collector and its number, leaving the flat cell's own. */
  _teardownCollect() {
    if (this._collectNumber && !this._collectNumber.destroyed) {
      this._collectNumber.destroy({ children: true });
    }
    this._collectNumber = null;
    this._collectTotal = null;

    const spine = this._collectSpine;
    this._collectSpine = null;
    if (!spine) return;
    this._setStripVisible(true);
    try {
      spine.destroy();
    } catch {
      // already gone
    }
  }

  /**
   * The track turning, as a LOOP — the same contract reel_spin has.
   *
   * ── One sound for the whole board, not one per cell ──
   *
   * A Golden Mystery opens a row of cells and every one of them spins its own
   * track. The loop describes THE COINS MOVING, which is a single thing the
   * player sees, so it starts when the first track starts and ends when the
   * last coin has landed — not once per cell.
   *
   * That is what the reference count in loopSoundRefs does, and it is the same
   * count the gamble scatter's strip uses. `_spinSoundOn` keeps this cell's own
   * half honest: acquire at most once, release exactly once, however the spin
   * ends.
   *
   * Idempotent on both sides, because a cell can be destroyed mid-spin — the
   * sequence is full of `if (this.destroyed) return` — and a reference never
   * given back would leave the loop playing for ever.
   */
  _startSpinSound() {
    if (this._spinSoundOn) return;
    this._spinSoundOn = true;
    acquireLoopSound(SOUND_NAME.golden_mystery_spin);
  }

  _stopSpinSound() {
    // FIRST, and before the early return: a start still waiting out the reveal
    // has taken no reference yet, so there is nothing for the lines below to
    // release — disarming it is the only thing that can stop it.
    this._spinSoundArmed = false;
    if (!this._spinSoundOn) return;
    this._spinSoundOn = false;
    releaseLoopSound(SOUND_NAME.golden_mystery_spin);
  }

  /**
   * Spin this cell again and land it on something new.
   *
   * A multiplier that fired and a coin that was collected both leave an empty
   * cell, and the server's next iteration says what drops into it. The track is
   * still there — it never went anywhere — so this is the same start/stopOn the
   * first landing used.
   *
   * @param {*} outcome What it lands on.
   * @param {number} [holdMs] How long it spins before being told to stop.
   */
  async respin(outcome, holdMs = 900, stopOrder = 0) {
    if (this.destroyed || !this.strip) return;
    this._setStripVisible(true);
    this.strip.start();
    // A refill is a spin too, and it joins the same count: a magnet can empty
    // four cells and they all respin together.
    this._startSpinSound();
    // A respin is a spin too, and skippable on the same terms.
    //
    // `stopOrder` is NOT decoration. The runner staggers a row of respins by
    // `index * stopStaggerMs` in board order, exactly as the first landing is
    // staggered — so they are a cascade and the skip has to follow it. This
    // used to pass 0 for every one of them, which left the registry falling
    // back to insertion order: right by accident, because Promise.all happens
    // to start them in board order, and silently wrong the day that changes.
    if (holdMs > 0) await this._waitForSpin(holdMs, stopOrder);
    if (this.destroyed) return;
    await this.strip.stopOn(outcome);
    this._stopSpinSound();
    // A refill is a landing. The cell a multiplier vacated or a magnet emptied
    // stops on a new symbol, and that symbol arrives the same way the first one
    // did — otherwise the second half of a chain is visibly deader than the
    // first.
    await this.playLandingBounce(outcome);
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  resize(cellWidth, cellHeight) {
    if (this.destroyed) return;
    // A zero size is the layout still measuring, not a cell — see the long note
    // in SymbolStripSpinner.resize. Taken seriously it divides this cell's
    // whole geometry by nothing: the fits, the lid scale, the mask and the
    // font size all collapse, and the strip inside can never be recovered.
    if (
      !Number.isFinite(cellWidth) ||
      !Number.isFinite(cellHeight) ||
      cellWidth <= 0 ||
      cellHeight <= 0
    ) {
      return;
    }
    if (this.cellWidth === cellWidth && this.cellHeight === cellHeight) return;

    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;
    // Both windows are re-fitted: the frame's, which holds the lid, and the
    // lid's, which holds the track. Each cancels its own bone chain against the
    // host's PIXI scale, and that scale has just changed.
    this._applyFrameFit();
    this.goldenSpine?.scale?.set(cellHeight / SYMBOL_REFERENCE_HEIGHT);
    this._applyFit();
    // Anything live over the cell — a firing multiplier, a collector holding its
    // total — is scaled by the cell too.
    this._collectSpine?.scale?.set(cellHeight / SYMBOL_REFERENCE_HEIGHT);
    this._live?.spine?.scale?.set(cellHeight / SYMBOL_REFERENCE_HEIGHT);
    this.strip?.resize(cellWidth, cellHeight);

    // ── Numbers are REBUILT, never scaled ──
    //
    // A number is text, and text is rasterised at the size its style names. Both
    // of these were built from the OLD cellHeight, and the worn one also
    // counter-scales against the spine scale that has just changed. Scaling the
    // container instead would stretch one rasterisation to a size it was not
    // drawn at — the soft, wrong-weight text this codebase has produced before,
    // and the reels rebuild theirs on resize for the same reason
    // (resizeAnimations recomputes every coin's font size rather than scaling
    // it).
    if (this._live?.decorate && this._live.spine) {
      try {
        this._live.worn?.destroy({ children: true });
      } catch {
        // already gone
      }
      this._live.worn = null;
      try {
        this._live.worn =
          this._live.decorate(this._live.parent, this._live.spine) ?? null;
      } catch (error) {
        console.error("[GoldenMystery] cell decoration resize failed:", error);
      }
    }
    if (this._collectNumber) {
      const total = this._collectTotal;
      try {
        this._collectNumber.destroy({ children: true });
      } catch {
        // already gone
      }
      this._collectNumber = null;
      if (total != null) {
        this._collectNumber =
          this.config?.buildCollectorNumber?.({
            parent: this.aboveEffects || this.effects,
            value: total,
            cellWidth,
            cellHeight,
          }) ?? null;
      }
    }
    if (this.maskGraphic) {
      this.maskGraphic.clear();
      this.maskGraphic.beginFill(0xffffff);
      this.maskGraphic.drawRect(
        -cellWidth / 2,
        -cellHeight / 2,
        cellWidth,
        cellHeight,
      );
      this.maskGraphic.endFill();
    }
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    // Off the registry at once — a dead entry would swallow the next press.
    releaseSkippableSpin(this._skipId);
    this._skipId = null;
    this._cancelPendingHide();

    // Ours, and not the cell's — it hangs off the overlay container, which
    // outlives this controller and belongs to the reel.
    if (this.aboveEffects && !this.aboveEffects.destroyed) {
      this.aboveEffects.destroy({ children: true });
    }
    this.aboveEffects = null;

    // ── Let every awaiter go, FIRST ──
    //
    // Before the listeners are removed, because removing them is what makes the
    // promises unresolvable. Each `play()` is guarded by `if (this.destroyed)
    // return` after every await, so releasing them here does not resume the
    // sequence — it only lets it unwind and hand the UI lock back.
    for (const settle of Array.from(this._pending)) settle();
    this._pending.clear();
    // Both go with the containers below — the spine as a child, the worn number
    // as its slot object. Dropped here so the released awaiters find nothing to
    // tear down twice.
    this._live = null;

    for (const { spine, listener } of this._listeners) {
      spine?.state?.removeListener?.(listener);
    }
    this._listeners = [];

    if (this._slotName && this.goldenSpine?.removeSlotObject) {
      try {
        this.goldenSpine.removeSlotObject(this._slotName, this.holder);
      } catch {
        // Slot object already gone with the spine — nothing to detach.
      }
    }
    if (this._frameSlotName && this.frameSpine?.removeSlotObject) {
      try {
        this.frameSpine.removeSlotObject(this._frameSlotName, this.frameHolder);
      } catch {
        // Same.
      }
    }
    this._teardownCollect();
    // A cell killed mid-spin still holds a reference. Without this the loop
    // outlives the feature — and a looping Howl nobody stops runs for ever.
    this._stopSpinSound();
    this.strip?.destroy();
    this.strip = null;
    if (this.holder) {
      // The mask goes before the destroy — a mask pointing at destroyed
      // Graphics is a crash.
      if (this.fit) this.fit.mask = null;
      this.holder.destroy({ children: true });
      this.holder = null;
      this.fit = null;
    }
    // The frame's holder takes the lid spine AND the effects container down with
    // it — both are its children.
    this.effects = null;
    if (this.frameHolder) {
      this.frameHolder.destroy({ children: true });
      this.frameHolder = null;
      this.frameFit = null;
    }
    this.goldenSpine = null;
    this.maskGraphic = null;
  }
}

export default GoldenMysteryCellController;
